import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { uuidRegEx } from "@impero-com/common/utils/uuid";
import { Svg } from "@impero-com/ui";
import * as Yup from "yup";

import { ConditionalAction, ConditionalOperator } from "api/generated/model";
import { Field } from "components/fieldBuilder/models";
import checkBoxSvg from "img/field/checkbox.svg";
import dropdownSvg from "img/field/dropdown.svg";
import fileSvg from "img/field/file.svg";
import headingSvg from "img/field/heading.svg";
import inputSvg from "img/field/input.svg";
import numberSvg from "img/field/number.svg";
import radioButtonSvg from "img/field/radiobutton.svg";
import textAreaSvg from "img/field/textarea.svg";
import {
    ConclusionFieldType,
    EffectivenessFieldType,
} from "org/controlTesting/testingProgram/view/components/ControlConclusionBuilder/schema";
import { CONSTANTS } from "utils/constants";
import { generate } from "utils/guid";

import { MAX_OPTION_LENGTH } from "./consts";
import { FieldDependencyLookupResult } from "./fieldBuilder";
import { DEFAULT_DRAWER_BEHAVIOR, DrawerBehavior } from "./fieldDrawer";
import * as theme from "./theme.css";

const MAX_BLOBS_PER_FIELD = CONSTANTS.COMMON_CONSTANTS.MAX_BLOBS_PER_FIELD;

export type CommonFieldType = Exclude<
    ConclusionFieldType,
    EffectivenessFieldType
>;
export type CommonFieldTypeName = CommonFieldType["type"];

/**
 * Note: We explicitly do not trim the option value.
 * Our existing data has options with leading and trailing spaces.
 * We do not want to change the existing data.
 *
 * Therefore we accept extra spaces and compare the options with condition values as they are.
 */
export function getOptionSchema() {
    return Yup.string()
        .required(t("FieldBuilder_Error_Options"))
        .min(1)
        .max(MAX_OPTION_LENGTH);
}
export function getCommonFieldTypeSchema(
    fieldType: CommonFieldType
): Yup.ISchema<CommonFieldType> {
    switch (fieldType.type) {
        case "CheckboxGroup":
        case "RadioButtonGroup":
        case "Dropdown": {
            return Yup.object({
                type: Yup.string().oneOf([fieldType.type]).defined(),
                options: Yup.array().defined().of(getOptionSchema()),
            }).defined();
        }
        case "FileUpload":
        case "NumericInput":
        case "TextInput":
        case "Heading":
        case "TextArea": {
            return Yup.object({
                type: Yup.string().oneOf([fieldType.type]).defined(),
            }).defined();
        }
        default: {
            return assertNever(fieldType);
        }
    }
}

export function getCommonFieldSchemaProperties() {
    return {
        id: Yup.string()
            .matches(uuidRegEx, {
                name: "uuid",
                message: t("Form_Error_Types_Regex", {
                    field: "id",
                    type: "UUID",
                }),
                excludeEmptyString: false,
            })
            .trim()
            .required(),
        title: Yup.string().trim().required(),
        instructions: Yup.string().defined(),
        blobIds: Yup.array()
            .defined()
            .of(Yup.string().defined())
            .max(
                MAX_BLOBS_PER_FIELD,
                t("FieldBuilder_Error_TooManyBlobs", {
                    limit: MAX_BLOBS_PER_FIELD,
                })
            ),
        mandatory: Yup.boolean().defined(),
        condition: Yup.object({
            action: Yup.mixed<ConditionalAction>()
                .oneOf(["Show", "Hide"])
                .defined(),
            operator: Yup.mixed<ConditionalOperator>()
                .oneOf(["Equal", "NotEqual"])
                .defined(),
            fieldId: Yup.string()
                .matches(uuidRegEx, {
                    name: "uuid",
                    message: t("Form_Error_Types_Regex", {
                        field: "id",
                        type: "UUID",
                    }),
                    excludeEmptyString: false,
                })
                .required(t("FieldBuilder_Error_Conditional_Field_Required")),
            value: Yup.string().required(
                t("FieldBuilder_Error_Conditional_Value_Required")
            ),
        })
            .defined()
            .nullable(),
    };
}

export function getCommonFieldTypeDefaultValue(
    fieldTypeName: CommonFieldTypeName,
    fieldType: CommonFieldType | undefined
): CommonFieldType {
    switch (fieldTypeName) {
        case "CheckboxGroup": {
            const options =
                fieldType?.type === "CheckboxGroup"
                    ? fieldType.options
                    : [
                          t("CreateControl_NewTask_Checkbox_Option1"),
                          t("CreateControl_NewTask_Checkbox_Option2"),
                          t("CreateControl_NewTask_Checkbox_Option3"),
                      ];
            return {
                type: fieldTypeName,
                options,
            };
        }
        case "Dropdown": {
            const options =
                fieldType?.type === "Dropdown"
                    ? fieldType.options
                    : [
                          t("CreateControl_NewTask_Dropdown_Option1"),
                          t("CreateControl_NewTask_Dropdown_Option2"),
                          t("CreateControl_NewTask_Dropdown_Option3"),
                      ];
            return {
                type: fieldTypeName,
                options,
            };
        }
        case "RadioButtonGroup": {
            const options =
                fieldType?.type === "RadioButtonGroup"
                    ? fieldType.options
                    : [
                          t("CreateControl_NewTask_RadioButtons_Option1"),
                          t("CreateControl_NewTask_RadioButtons_Option2"),
                      ];

            return {
                type: fieldTypeName,
                options,
            };
        }
        case "FileUpload":
        case "NumericInput":
        case "TextInput":
        case "TextArea":
        case "Heading": {
            return {
                type: fieldTypeName,
            };
        }
        default: {
            return assertNever(fieldTypeName);
        }
    }
}

export function getCommonFieldProperties() {
    return {
        id: generate(),
        instructions: "",
        blobIds: [],
        mandatory: false,
        condition: null,
    };
}

export function getCommonFieldTypeInfo(): Record<
    CommonFieldTypeName,
    FieldTypeInfo
> {
    return {
        CheckboxGroup: {
            label: t("CreateControl_TaskType_Checkbox"),
            svg: checkBoxSvg,
        },
        FileUpload: {
            label: t("CreateControl_TaskType_File"),
            svg: fileSvg,
        },
        NumericInput: {
            label: t("CreateControl_TaskType_Numeric"),
            svg: numberSvg,
        },
        RadioButtonGroup: {
            label: t("CreateControl_TaskType_RadioButtons"),
            svg: radioButtonSvg,
        },
        TextInput: {
            label: t("CreateControl_TaskType_SingleLineText"),
            svg: inputSvg,
        },
        TextArea: {
            label: t("CreateControl_TaskType_Text"),
            svg: textAreaSvg,
        },
        Dropdown: {
            label: t("CreateControl_TaskType_Dropdown"),
            svg: dropdownSvg,
        },
        Heading: {
            label: t("CreateControl_TaskType_Heading"),
            svg: headingSvg,
        },
    };
}

export function findDependency<F extends Field<unknown, unknown>>(
    dependencyId: string,
    fields: F[],
    fieldId: string
): FieldDependencyLookupResult<F> {
    let after = false;
    for (const field of fields) {
        if (field.id !== fieldId) {
            if (field.id === dependencyId) {
                return {
                    type: after ? "FOUND_AFTER" : "FOUND",
                    value: field,
                };
            }
        } else {
            after = true;
        }
    }
    return { type: "NOT_FOUND" };
}

export function getCommonFieldTypeDrawerBehavior(
    fieldType: CommonFieldType
): DrawerBehavior {
    switch (fieldType.type) {
        case "Heading": {
            return {
                ...DEFAULT_DRAWER_BEHAVIOR,
                hideRequired: true,
            };
        }
        case "CheckboxGroup":
        case "RadioButtonGroup":
        case "Dropdown":
        case "FileUpload":
        case "NumericInput":
        case "TextInput":
        case "TextArea": {
            return DEFAULT_DRAWER_BEHAVIOR;
        }
        default: {
            return assertNever(fieldType);
        }
    }
}

export function getCommonFieldTypeConditionalValues(
    fieldType: CommonFieldType
) {
    switch (fieldType.type) {
        case "CheckboxGroup":
        case "RadioButtonGroup":
        case "Dropdown": {
            return fieldType.options;
        }
        case "FileUpload":
        case "NumericInput":
        case "TextInput":
        case "TextArea":
        case "Heading": {
            return [];
        }
        default: {
            return assertNever(fieldType);
        }
    }
}

export const FieldPlaceholderCmp = () => {
    return <div className={theme.fieldPlaceholder} />;
};

export interface FieldTypeInfo {
    label: string;
    svg: string;
}

export function renderPlaceholder(info: FieldTypeInfo) {
    return (
        <div className={theme.fieldPlaceholder}>
            <Svg src={info.svg} className={theme.fieldIcon} />
            {info.label}
        </div>
    );
}
